<template>
  <div class="container-fluid p-4">
    <div class="box">
      <!-- Header Section: Title and Dropdown -->
      <div class="row mb-3">
        <div class="col-12">
          <div :class="isMobile ? 'd-flex flex-column align-items-start' : 'd-flex justify-content-between align-items-center'">
            <h2 :class="{ 'mb-3': isMobile }">{{ $t('eventOrganizers.titleList') }}</h2>

            <!-- Master Type Dropdown (top-right on desktop, below title on mobile) -->
            <div class="dropdown" :class="[{ 'mb-3': isMobile }, { show: isDropdownOpen }]">
              <button
                class="btn btn-outline-secondary dropdown-toggle"
                type="button"
                :aria-expanded="isDropdownOpen"
                @click="toggleDropdown">
                {{ $t('eventOrganizers.title') }}
              </button>
              <ul class="dropdown-menu" :class="{ show: isDropdownOpen }">
                <li>
                  <a class="dropdown-item active"
                    href="/master/event_organizers"
                    @click.prevent="changeType('event_organizer')">
                    {{ $t('eventOrganizers.title') }}
                  </a>
                </li>
                <li>
                  <a class="dropdown-item"
                    href="/master/events"
                    @click.prevent="changeType('event')">
                    {{ $t('masterEvents.title') }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Box and New Button -->
      <div class="row mb-3">
        <div :class="isMobile ? 'col-12 mb-3' : 'col-md-6'">
          <div class="search-box">
            <input
              type="text"
              class="form-control search-input"
              v-model="filter.name"
              :placeholder="$t('eventOrganizers.searchPlaceholder')"
              @input="handleSearchInput"
            />
            <i class="bi bi-search search-icon"></i>
          </div>
        </div>
        <div :class="isMobile ? 'col-12' : 'col-md-6 text-end'">
          <button class="btn btn-primary" @click="createNew">
            <i class="bi bi-plus-circle"></i> {{ $t('eventOrganizers.addNew') }}
          </button>
        </div>
      </div>

      <!-- Table with horizontal scroll -->
      <div class="table-wrapper">
        <div v-if="page" class="table-scroll">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>{{ $t('eventOrganizers.organizer') }}</th>
                <th>{{ $t('eventOrganizers.phoneMain') }}</th>
                <th>{{ $t('eventOrganizers.phoneSub') }}</th>
                <th>{{ $t('eventOrganizers.emailMain') }}</th>
                <th>{{ $t('eventOrganizers.emailSub') }}</th>
                <th>{{ $t('eventOrganizers.postalCode') }}</th>
                <th>{{ $t('eventOrganizers.prefecture') }}</th>
                <th>{{ $t('eventOrganizers.city') }}</th>
                <th>{{ $t('eventOrganizers.cityDetail') }}</th>
                <th>{{ $t('eventOrganizers.building') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="organizer in page.items"
                :key="organizer.id"
                @click="editOrganizer(organizer.id)"
                class="cursor-pointer"
              >
                <td>{{ organizer.name }}</td>
                <td>{{ organizer.phonePrimary || '-' }}</td>
                <td>{{ organizer.phoneSecondary || '-' }}</td>
                <td>{{ organizer.emailPrimary || '-' }}</td>
                <td>{{ organizer.emailSecondary || '-' }}</td>
                <td>{{ organizer.postalCode || '-' }}</td>
                <td>{{ organizer.prefecture || '-' }}</td>
                <td>{{ organizer.city || '-' }}</td>
                <td>{{ organizer.town || '-' }}</td>
                <td>{{ organizer.building || '-' }}</td>
              </tr>
              <tr v-if="page.items.length === 0">
                <td colspan="10" class="text-center text-muted py-4">
                  {{ $t('eventOrganizers.noData') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center py-4">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">{{ $t('eventOrganizers.loading') }}</span>
          </div>
        </div>

        <!-- Pagination -->
        <SimplePagination
          v-if="page"
          :page="page"
          @change="changePage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import EventOrganizerFilter from '../../../../filters/user/EventOrganizerFilter';
import usePagination from '../../../../composables/usePagination';
import SimplePagination from '../../../../components/SimplePagination.vue';
import { useIsMobile } from '../../../../utils/DeviceUtil';
import { colors } from '../../../../../consts/colors';

const router = useRouter();
const filter = ref(new EventOrganizerFilter());
const { isMobile } = useIsMobile();

const { page, search, changePage } = usePagination(filter.value);

// Debounce timer for search
const searchTimer = ref(null);

// Current master data type
const currentType = ref('event_organizer');

// Dropdown state
const isDropdownOpen = ref(false);

/**
 * Toggle dropdown menu
 */
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

/**
 * Close dropdown when clicking outside
 */
const handleClickOutside = (event) => {
  const dropdown = document.querySelector('.dropdown');
  if (dropdown && !dropdown.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

// Add/remove event listener for clicking outside
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

/**
 * Change master data type
 * @param {string} type - Master data type ('event_organizer', 'hotel', etc.)
 */
const changeType = (type) => {
  // Close dropdown
  isDropdownOpen.value = false;

  if (currentType.value === type) {
    // Already on this type, do nothing
    return;
  }

  currentType.value = type;

  // Navigate to different master data pages based on type
  switch (type) {
    case 'event_organizer':
      // Already on this page
      break;
    case 'event':
      router.push({ name: 'master.events.index' });
      break;
    default:
      console.warn(`Unknown master data type: ${type}`);
      break;
  }
};

/**
 * Handle search input with debounce
 */
const handleSearchInput = () => {
  // Clear existing timer
  if (searchTimer.value) {
    clearTimeout(searchTimer.value);
  }

  // Set new timer to execute search after 200ms
  searchTimer.value = setTimeout(() => {
    search();
  }, 200);
};

const createNew = () => {
  router.push({ name: 'master.event_organizers.new' });
};

const editOrganizer = (id) => {
  router.push({ name: 'master.event_organizers.edit', params: { id } });
};
</script>

<style lang="scss" scoped>
.container-fluid {
  max-width: 1400px;
}

h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: v-bind('colors.textPrimary');
}

.box {
  background-color: v-bind('colors.white');
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px v-bind('colors.shadowColor');
}

// Search box with icon
.search-box {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding-right: 40px;
  border-color: v-bind('colors.border');

  &:focus {
    border-color: v-bind('colors.blue[500]');
    box-shadow: 0 0 0 0.2rem v-bind('colors.shadowPrimary');
  }
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: v-bind('colors.gray[500]');
  font-size: 16px;
  pointer-events: none;
}

// Buttons
.btn-primary {
  background-color: v-bind('colors.blue[500]');
  border-color: v-bind('colors.blue[500]');
  padding: 0.5rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Dropdown button
.dropdown {
  .btn-outline-secondary {
    color: v-bind('colors.gray[600]');
    border-color: v-bind('colors.border');
    padding: 0.5rem 1rem;
    font-size: 0.95rem;

    &:hover {
      background-color: v-bind('colors.gray[50]');
      border-color: v-bind('colors.gray[400]');
      color: v-bind('colors.gray[700]');
    }
  }

  .dropdown-menu {
    border-color: v-bind('colors.border');
    box-shadow: 0 2px 8px v-bind('colors.shadowColor');

    .dropdown-item {
      color: v-bind('colors.textPrimary');

      &:hover {
        background-color: v-bind('colors.gray[50]');
      }

      &.active {
        background-color: v-bind('colors.blue[500]');
        color: v-bind('colors.white');
      }
    }
  }
}

// Table styles
.table {
  margin-bottom: 0;
  font-size: 0.95rem;

  thead th {
    border-bottom: 2px solid v-bind('colors.gray[200]');
    font-weight: 600;
    color: v-bind('colors.textPrimary');
    background-color: v-bind('colors.gray[50]');
    padding: 0.75rem;
    white-space: nowrap;

    // Column widths
    &:nth-child(1) { min-width: 300px; } // 主催者
    &:nth-child(2) { min-width: 200px; } // 電話番号(メイン)
    &:nth-child(3) { min-width: 200px; } // 電話番号(サブ)
    &:nth-child(4) { min-width: 300px; } // メールアドレス(メイン)
    &:nth-child(5) { min-width: 300px; } // メールアドレス(サブ)
    &:nth-child(6) { min-width: 120px; } // 郵便番号
    &:nth-child(7) { min-width: 120px; }  // 都道府県
    &:nth-child(8) { min-width: 240px; } // 市区町村
    &:nth-child(9) { min-width: 500px; } // 市区町村以降
    &:nth-child(10) { min-width: 500px; } // 建物名
  }

  tbody {
    td {
      padding: 0.75rem;
      color: v-bind('colors.textSecondary');
      vertical-align: middle;

      // Column widths (same as thead)
      &:nth-child(1) { min-width: 200px; } // 主催者
      &:nth-child(2) { min-width: 130px; } // 電話番号(メイン)
      &:nth-child(3) { min-width: 130px; } // 電話番号(サブ)
      &:nth-child(4) { min-width: 200px; } // メールアドレス(メイン)
      &:nth-child(5) { min-width: 200px; } // メールアドレス(サブ)
      &:nth-child(6) { min-width: 100px; } // 郵便番号
      &:nth-child(7) { min-width: 80px; }  // 都道府県
      &:nth-child(8) { min-width: 120px; } // 市区町村
      &:nth-child(9) { min-width: 150px; } // 市区町村以降
      &:nth-child(10) { min-width: 150px; } // 建物名
    }

    .cursor-pointer {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: v-bind('colors.bgHover');
      }
    }
  }

  .text-muted {
    color: v-bind('colors.gray[500]') !important;
  }
}

// Loading spinner
.spinner-border {
  color: v-bind('colors.blue[500]');
}

// Table wrapper with horizontal scroll and pagination
.table-wrapper {
  position: relative;
}

.table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: v-bind('colors.gray[100]');
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: v-bind('colors.gray[400]');
    border-radius: 4px;

    &:hover {
      background: v-bind('colors.gray[500]');
    }
  }
}

// Ensure table doesn't collapse
.table-scroll table {
  min-width: 1500px;
  margin-bottom: 0;
}
</style>
